import { useMatch } from 'react-router-dom';

import type { Bonus2026FlyInTriggerProps } from '../Bonus2026FlyIn/Bonus2026FlyInTrigger.types';

import { appConfig } from '../../../../appConfig';
import MainPageLayout from '../../../base/components/MainPageLayout';
import MunicipalNotice from '../../../base/components/MunicipalNotice';
import PunchoutNotice from '../../../base/components/PunchoutNotice';
import { EditOrderModeNoticeGoToCheckout } from '../../../common/components/atoms/EditOrderModeNotice';
import { useIsDesktop } from '../../../common/components/atoms/ResponsiveBreakpoints';
import { useIsEcommerceBonusTriggerEnabled } from '../../../common/hooks/bonus2026FeatureFlagHooks';
import { useAppSelector } from '../../../common/hooks/useThunkDispatch';
import { storeSelectors } from '../../../common/selectors/storeSelectors';
import { ga4NavigationTracking } from '../../../common/tracking/ga4/navigation';
import { ProductSectionsContextProvider } from '../../contexts/productSectionsContext';
import { useAbTest } from '../../hooks/useAbTests';
import Bonus2026FlyIn from '../Bonus2026FlyIn/Bonus2026FlyIn';
import Bonus2026FlyInTrigger from '../Bonus2026FlyIn/Bonus2026FlyInTrigger';
import Bonus2026FlyInTriggerWrapper from '../Bonus2026FlyIn/Bonus2026FlyInTriggerWrapper';
import EcommerceInContentNotices from '../EcommerceInContentNotices';
import { EcommerceSideMenu } from '../EcommerceSideMenu';
import PriceTypeSwitch from '../PriceTypeSwitch';
import { PriceTypeSwitchStyledContainer } from '../PriceTypeSwitch/PriceTypeSwitch';
import SectionMenuPortal from '../SectionMenu';
import InPageContentTimeslotPreview from '../TimeslotPreview/InPageContentTimeslotPreview';
import styles from './EcommercePageLayout.module.scss';

const PriceSwitch = () => {
    const isPunchout = useAppSelector((state) => state.user.punchout?.isPunchout);

    return (
        !isPunchout && (
            <div className={styles.PriceTypeSwitch}>
                <span>Visa priser</span>
                <PriceTypeSwitchStyledContainer>
                    <PriceTypeSwitch ariaLabel="Visa Priser" />
                </PriceTypeSwitchStyledContainer>
            </div>
        )
    );
};

type EcommerceBonusTriggerProps = {
    enabled: boolean;
    wrapperClassName: string;
    label: string;
    splashSize?: Bonus2026FlyInTriggerProps['splashSize'];
};

const EcommerceBonusTrigger = ({
    enabled,
    wrapperClassName,
    label,
    splashSize = 'default',
}: EcommerceBonusTriggerProps) => {
    if (!enabled) {
        return null;
    }

    return (
        <Bonus2026FlyIn
            renderTrigger={(ref, open) => (
                <Bonus2026FlyInTriggerWrapper className={wrapperClassName}>
                    {(onError) => (
                        <Bonus2026FlyInTrigger
                            ref={ref}
                            onClick={open}
                            onError={onError}
                            label={label}
                            splashSize={splashSize}
                        />
                    )}
                </Bonus2026FlyInTriggerWrapper>
            )}
        />
    );
};

const EcommercePageLayoutSideMenu = (props: { ecommercePageContentId?: number }) => {
    const isEcommerceBonusTriggerEnabled = useIsEcommerceBonusTriggerEnabled();
    const isDesktop = useIsDesktop();

    const onSideMenuLinkClick = (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
        name: string | null,
    ) => {
        ga4NavigationTracking.sideMenu.click(name || '', e.currentTarget.href);
    };

    return (
        <div className="SidebarNav">
            <div className="SidebarNav-container">
                <div className="SidebarNav-content">
                    <EcommerceBonusTrigger
                        enabled={isEcommerceBonusTriggerEnabled && isDesktop}
                        wrapperClassName={styles.BonusTriggerSideMenuWrapper}
                        label="Månadsbonus"
                        splashSize="small"
                    />
                    <EcommerceSideMenu
                        ecommercePageContentId={props.ecommercePageContentId}
                        onLastLevelLinkClick={onSideMenuLinkClick}
                        onLinkClick={onSideMenuLinkClick}
                    />
                    <SectionMenuPortal
                        onLastLevelLinkClick={onSideMenuLinkClick}
                        onLinkClick={onSideMenuLinkClick}
                    />
                </div>
            </div>
        </div>
    );
};

const EcommercePageLayout = (
    props: React.PropsWithChildren<{
        ecommercePageContentId?: number;
    }>,
) => {
    const isB2BStartPage = !!useMatch(appConfig.coopSettings.b2b.ecommerce.page!.url);
    const isB2CStartPage = !!useMatch(appConfig.coopSettings.ecommerce.page!.url);
    const isB2CSearchPage = !!useMatch(appConfig.coopSettings.ecommerce.search.url);
    const isB2BSearchPage = !!useMatch(appConfig.coopSettings.b2b.ecommerce.search.url);
    const hasActiveStoreSelection = useAppSelector(storeSelectors.hasActiveStoreSelection);
    const isEcommerceBonusTriggerEnabled = useIsEcommerceBonusTriggerEnabled();
    const isDesktop = useIsDesktop();
    const { isVariant: isStartPageVariant } = useAbTest({
        name: 'startpage_timepicker_abtest',
        enabled: (isB2BStartPage || isB2CStartPage) && hasActiveStoreSelection,
    });

    const { isVariant: isSearchPageVariant } = useAbTest({
        name: 'searchpage_timepicker_abtest',
        enabled: (isB2BSearchPage || isB2CSearchPage) && hasActiveStoreSelection,
    });

    return (
        <ProductSectionsContextProvider>
            <MainPageLayout
                sideMenuSlot={
                    <EcommercePageLayoutSideMenu
                        ecommercePageContentId={props.ecommercePageContentId}
                    />
                }
                subHeroSlot={
                    <EcommerceInContentNotices>
                        <MunicipalNotice />
                        <PunchoutNotice />
                        <EditOrderModeNoticeGoToCheckout />
                    </EcommerceInContentNotices>
                }
            >
                <div className="EcommerceContentPage u-posRelative">
                    <div className={styles.TopContent}>
                        {!isStartPageVariant && !isSearchPageVariant && (
                            <InPageContentTimeslotPreview />
                        )}
                        {isB2BStartPage && <PriceSwitch />}
                        <EcommerceBonusTrigger
                            enabled={isEcommerceBonusTriggerEnabled && !isDesktop}
                            wrapperClassName={styles.BonusTriggerWrapper}
                            label="Månadsbonus just nu"
                        />
                    </div>
                    {props.children}
                </div>
            </MainPageLayout>
        </ProductSectionsContextProvider>
    );
};

export default EcommercePageLayout;
